import { formatDate, formatDateToYYYYMMDD } from './DateUtils';
import { toWorkDayFromInput, WEEK_DAY_LONG_LABELS } from './WorkDayUtils';

/**
 * Datas especiais das filiais: dias em que elas fogem do horário semanal, fechando (feriado) ou
 * abrindo em horário especial. O cadastro fica em Agendamentos > Feriados e horários; a agenda e o
 * link público usam estas funções para tratar o dia do mesmo jeito que o backend trata na validação.
 *
 * Formato: { specialDate: 'yyyy-MM-dd', isOpen, openTime, closeTime, breakStartTime?, breakEndTime?,
 * reason, branchIds?, branchNames? }. A API administrativa diz em quais filiais a data vale
 * (branchIds); a pública já entrega as datas dentro de cada filial e não traz a lista.
 */

/** "09:00:00" vira "09:00". */
const shortTime = (time) => (time ? String(time).slice(0, 5) : '');

/** Data do JS ou string do backend no formato yyyy-MM-dd, sem deslocar o dia por UTC. */
const toIsoDate = (date) => (date instanceof Date ? formatDateToYYYYMMDD(date) : String(date || '').slice(0, 10));

/**
 * Datas especiais do dia. Com filial informada, só as que valem para ela; sem filial (agenda em
 * "todas as filiais"), as de todas as unidades carregadas.
 */
export const specialDaysOn = (specialDays, date, branchId = null) => {
    if (!Array.isArray(specialDays) || !date) {
        return [];
    }

    const isoDate = toIsoDate(date);
    return specialDays.filter((day) => day.specialDate === isoDate
        && (branchId == null || !Array.isArray(day.branchIds) || day.branchIds.includes(branchId)));
};

/** A primeira data especial do dia, ou null. */
export const findSpecialDay = (specialDays, date, branchId = null) => specialDaysOn(specialDays, date, branchId)[0] || null;

/** "Fechada" ou "09:00 às 14:00". */
export const specialDayHoursLabel = (day) => (day?.isOpen
    ? `${shortTime(day.openTime)} às ${shortTime(day.closeTime)}`
    : 'Fechada');

/** "Segunda-feira, 02/11/2026", com o dia da semana em maiúscula como no resto do sistema. */
export const specialDayDateLabel = (date) => {
    const isoDate = toIsoDate(date);
    return `${WEEK_DAY_LONG_LABELS[toWorkDayFromInput(isoDate)] || ''}, ${formatDate(isoDate)}`;
};

/**
 * Data especial no formato do horário semanal da filial (BranchWorkingHours), para a agenda
 * aplicar a mesma conta de janela e de dia fechado que já faz com a semana.
 */
export const specialDayAsWorkingHours = (day) => ({
    isOpen: Boolean(day.isOpen),
    openTime: day.isOpen ? day.openTime : null,
    closeTime: day.isOpen ? day.closeTime : null,
    breakStartTime: day.isOpen ? day.breakStartTime ?? null : null,
    breakEndTime: day.isOpen ? day.breakEndTime ?? null : null,
    specialDay: day
});
